/**
 * ClassicTemplate - Traditional formal layout, modernized
 * Visual clássico com header de capa, foto sobreposta, cards com sombra suave
 */

import { Award, MapPin, Hash, CheckCircle, FileText, Users, Image } from 'lucide-react';
import { SantinhoTemplateProps, formatMandate, hasSocialLinks } from './types';
import { SocialLinks } from './SocialLinks';
import { GallerySection } from './GallerySection';
import { ApprovedProjectsSection } from './ApprovedProjectsSection';
import { ContactRegistrationButton } from '../ContactRegistrationButton';
import { WebchatWidget } from '../../webchat';

export function ClassicTemplate({ tenant }: SantinhoTemplateProps) {
  const { publicPage, theme, name } = tenant;
  const { title, description, content, socialLinks } = publicPage;

  return (
    <div
      className="min-h-screen bg-gray-50"
      style={{ fontFamily: theme.fontFamily || 'inherit' }}
    >
      {/* Cover Header */}
      <header className="relative h-56 md:h-72" style={{ backgroundColor: theme.primaryColor }}>
        {content.coverPhoto && (
          <img
            src={content.coverPhoto}
            alt={name}
            className="w-full h-full object-cover absolute inset-0"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/70" />
        {/* Decorative corner accent */}
        <div
          className="absolute top-0 left-0 w-32 h-32 opacity-20"
          style={{
            background: `radial-gradient(circle at top left, white, transparent)`,
          }}
        />
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 -mt-20 relative z-10 pb-16">
        <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] overflow-hidden">

          {/* Profile Section */}
          <div className="flex flex-col md:flex-row">
            {/* Left Column */}
            <div className="md:w-80 flex-shrink-0 p-8 flex flex-col items-center md:border-r border-gray-100 bg-gradient-to-b from-gray-50 to-white">
              {/* Candidate Photo */}
              <div className="relative -mt-28 mb-4">
                {content.candidatePhoto ? (
                  <img
                    src={content.candidatePhoto}
                    alt={name}
                    className="w-40 h-40 rounded-2xl object-cover shadow-xl border-4 border-white"
                    style={{ boxShadow: `0 8px 32px ${theme.primaryColor}40` }}
                  />
                ) : theme.logoUrl ? (
                  <img
                    src={theme.logoUrl}
                    alt={name}
                    className="w-40 h-40 rounded-2xl object-cover bg-white shadow-xl border-4 border-white"
                  />
                ) : (
                  <div
                    className="w-40 h-40 rounded-2xl shadow-xl border-4 border-white flex items-center justify-center text-5xl font-bold text-white"
                    style={{ backgroundColor: theme.primaryColor }}
                  >
                    {name.charAt(0)}
                  </div>
                )}
              </div>

              {/* Candidate Number */}
              {content.candidateNumber && (
                <div
                  className="mt-2 px-7 py-3 rounded-xl text-white font-bold text-3xl tracking-wider shadow-md transition-transform hover:scale-105"
                  style={{ backgroundColor: theme.primaryColor }}
                >
                  <div className="flex items-center gap-2">
                    <Hash className="w-5 h-5 opacity-70" />
                    <span>{content.candidateNumber}</span>
                  </div>
                </div>
              )}

              {/* Divider */}
              <div
                className="w-12 h-0.5 my-6 rounded-full"
                style={{ backgroundColor: theme.primaryColor, opacity: 0.3 }}
              />

              {/* Social Links */}
              {hasSocialLinks(socialLinks) && (
                <div className="w-full">
                  <SocialLinks links={socialLinks} color={theme.primaryColor} />
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="flex-1 p-8">
              {/* Title */}
              <div className="pt-4 md:pt-0">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight">
                  {title || name}
                </h1>
                {description && (
                  <p className="text-gray-500 mt-2 text-lg leading-relaxed">{description}</p>
                )}
                <div
                  className="w-16 h-1 mt-4 rounded-full"
                  style={{ backgroundColor: theme.primaryColor }}
                />
              </div>

              {/* Biography */}
              {content.biography && (
                <div className="mt-8">
                  <h2
                    className="text-lg font-semibold mb-4 flex items-center gap-2"
                    style={{ color: theme.primaryColor }}
                  >
                    <Users className="w-5 h-5" />
                    Sobre
                  </h2>
                  <div className="space-y-3">
                    {content.biography.split('\n').filter(Boolean).map((p, i) => (
                      <p key={i} className="text-gray-600 leading-relaxed">
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Mandates */}
              {content.mandates && content.mandates.length > 0 && (
                <div className="mt-8">
                  <h2
                    className="text-lg font-semibold mb-4 flex items-center gap-2"
                    style={{ color: theme.primaryColor }}
                  >
                    <Award className="w-5 h-5" />
                    Experiência Política
                  </h2>
                  <ul className="space-y-2">
                    {content.mandates.map((mandate, i) => (
                      <li
                        key={i}
                        className="flex items-center gap-3 text-gray-700 py-2 px-4 rounded-lg bg-gray-50 border-l-4 transition-colors hover:bg-gray-100"
                        style={{ borderColor: theme.primaryColor }}
                      >
                        <MapPin
                          className="w-4 h-4 flex-shrink-0"
                          style={{ color: theme.primaryColor }}
                        />
                        <span className="text-sm font-medium">{formatMandate(mandate)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Proposals Section */}
          {content.proposals && content.proposals.length > 0 && (
            <div className="border-t border-gray-100 p-8">
              <h2
                className="text-xl font-bold mb-6 flex items-center gap-2"
                style={{ color: theme.primaryColor }}
              >
                <FileText className="w-5 h-5" />
                Propostas
              </h2>
              <ul className="grid md:grid-cols-2 gap-4">
                {content.proposals.map((proposal, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-4 p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div
                      className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-sm"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-gray-700 pt-1 leading-relaxed">{proposal}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Achievements Section */}
          {content.achievements && content.achievements.length > 0 && (
            <div className="border-t border-gray-100 p-8 bg-gray-50/50">
              <h2
                className="text-xl font-bold mb-6 flex items-center gap-2"
                style={{ color: theme.primaryColor }}
              >
                <CheckCircle className="w-5 h-5" />
                Conquistas
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {content.achievements.map((achievement, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-xl p-4 border-l-4 shadow-sm hover:shadow-md transition-shadow"
                    style={{ borderColor: theme.primaryColor }}
                  >
                    <p className="text-gray-700 leading-relaxed">{achievement}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Approved Projects Section */}
          {content.approvedProjects && content.approvedProjects.length > 0 && (
            <div className="border-t border-gray-100 p-8">
              <h2
                className="text-xl font-bold mb-6 flex items-center gap-2"
                style={{ color: theme.primaryColor }}
              >
                <FileText className="w-5 h-5" />
                Projetos Aprovados
              </h2>
              <ApprovedProjectsSection
                projects={content.approvedProjects}
                primaryColor={theme.primaryColor}
                variant="cards"
              />
            </div>
          )}

          {/* Gallery Section */}
          {content.gallery && content.gallery.length > 0 && (
            <div className="border-t border-gray-100 p-8">
              <h2
                className="text-xl font-bold mb-6 flex items-center gap-2"
                style={{ color: theme.primaryColor }}
              >
                <Image className="w-5 h-5" />
                Galeria
              </h2>
              <GallerySection images={content.gallery} />
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 mt-4" style={{ backgroundColor: theme.secondaryColor }}>
        <div className="container mx-auto px-4 text-center">
          <p className="text-white/70 text-sm">
            Página criada com{' '}
            <a
              href="https://campanhaflow.com.br"
              className="text-white hover:underline font-medium"
            >
              CampanhaFlow
            </a>
          </p>
        </div>
      </footer>

      {/* Contact Registration Button */}
      <ContactRegistrationButton />

      {/* Webchat widget (only renders when enabled for this tenant) */}
      <WebchatWidget tenant={tenant} />
    </div>
  );
}
